import React from 'react';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import moment from 'moment';
import getOr from '~/util/getOr';
import normalise from '~/schemas/teamStats/normalise';
import { MLB_BATTING, MLB_OVERVIEW, MLB_PITCHING } from '~/schemas/teamStats/mlbSchema';

import Matchup from '~/components/Matchup/Matchup';
import TeamStats from '~/components/TeamStats/TeamStats';
import GameDetailsCard from '~/components/GameDetailsCard/GameDetailsCard';
import KeyPlayersCard from '~/components/KeyPlayersCard/KeyPlayersCard';
import LineScoreCard from '~/components/LineScoreCard/LineScoreCard';
import TeamComparisonCard from '~/components/TeamComparisonCard/TeamComparisonCard';
import TeamStatsGetTheApp from '~/components/TeamStatsGetTheApp/TeamStatsGetTheApp';
import ScoringSummary from '~/components/ScoringSummary/ScoringSummary';
import MatchupSection from '~/components/MatchupSection/MatchupSection';
import BaseballBattingLineup from '~/components/BaseballBattingLineup/BaseballBattingLineup';
import Ad, { AdTypes } from '~/containers/AdContainer/AdContainer';

import getPlayerRecords from '~/util/getPlayerRecords';
import formatKeyPlayers from '~/util/formatKeyPlayers';
import normalizeBaseballEvent from '~/util/normalizeBaseballEvent';
import StyledComponent from '~/components/Styled/Styled';
import { injectIntl, intlShape } from 'react-intl';

import css from '~/components/MLBMatchup/MLBMatchup.scss';
import ActivePlayer from '~/components/ActivePlayer/ActivePlayer';

const adjustEventDetails = event => {
  let eventDetails = event.event_details;
  const preGame = event.event_status === 'pre_game';
  if (preGame) {
    eventDetails = filter(
      eventDetails,
      detail =>
        !detail.identifier.includes('broadcast') && !detail.identifier.includes('previous_matchup')
    );
    eventDetails.push({
      label: 'Start Time',
      identifier: 'start_time',
      content: moment(event.game_date).format('h:mm A MMM D'),
    });
  }
  return eventDetails;
};

const renderLineScoreCard = event => (
  <MatchupSection>
    <LineScoreCard event={event} />
  </MatchupSection>
);

const renderKeyPlayersCard = (keyPlayers, title, sport, status) => {
  if (!keyPlayers.players) {
    return <div />;
  }

  return (
    <MatchupSection>
      <KeyPlayersCard
        stats={keyPlayers.stats}
        players={keyPlayers.players}
        statsRecords={keyPlayers.statsRecords}
        title={title}
        sport={sport}
        status={status}
      />
    </MatchupSection>
  );
};

const renderGameDetailsCard = event => (
  <MatchupSection>
    <GameDetailsCard eventDetails={adjustEventDetails(event)} />
  </MatchupSection>
);

const renderTeamComparisonCard = event => (
  <MatchupSection>
    <TeamComparisonCard event={event} />
  </MatchupSection>
);

const renderLastGame = lastGame => (
  <MatchupSection>
    <div className={css.headerBox}>
      <div className={css.headerBoxText}>Last Game</div>
    </div>
    {lastGame ? (
      <div className={css.lastGameText}>{lastGame}</div>
    ) : (
      <div className={css.lastGameText}>None</div>
    )}
  </MatchupSection>
);

const renderTeamComparisonAndLastGame = (event, lastGame) => (
  <MatchupSection>
    <MatchupSection>{renderLastGame(lastGame)}</MatchupSection>
    {renderTeamComparisonCard(event)}
  </MatchupSection>
);

const renderTeamStats = (event, stats, title) => (
  <MatchupSection>
    <div className={css.cardHeader}>{title}</div>
    <div className={css.teamStats}>
      <TeamStats
        stats={stats}
        homeTeamName={getOr(event, 'home_team.abbreviation', '')}
        awayTeamName={getOr(event, 'away_team.abbreviation', '')}
      />
    </div>
  </MatchupSection>
);

const renderMatchupCard = event => (
  <MatchupSection>
    <Matchup event={event} />
  </MatchupSection>
);

const renderScoringSummary = event => (
  <MatchupSection>
    <h2 className={css.cardHeader}>Scoring Summary</h2>
    <ScoringSummary scorings={event.scoring_plays} sportType="baseball" />
  </MatchupSection>
);

class MLBMatchup extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      battingLineup: {},
    };
  }

  componentDidMount() {
    if (this.props.event.event_status === 'pre_game') {
      this.loadPlayerRecords();
    }
  }

  async loadPlayerRecords() {
    /* prettier-ignore */
    const {
      event: { box_score, league },
    } = this.props;

    let battingLineup;

    if (this.props.event.box_score) {
      battingLineup = await getPlayerRecords(league, box_score.id);

      const battingLineupToParse = battingLineup.find(lineup => lineup.type === 'batting');

      if (battingLineupToParse && battingLineupToParse.stats.length > 0) {
        this.setState({
          battingLineup: battingLineupToParse.stats,
        });
      }
    }
  }

  renderAllTeamStats(event) {
    return (
      <div className={css.teamStatsCard}>
        <div className={css.teamStats}>
          {renderTeamStats(
            event,
            normalise(this.props.stats.team, MLB_OVERVIEW),
            'Overview (per game)'
          )}
          {renderTeamStats(event, normalise(this.props.stats.team, MLB_BATTING), 'Batting/Running')}
          {renderTeamStats(
            event,
            normalise(this.props.stats.team, MLB_PITCHING),
            'Pitching/Defense'
          )}
        </div>

        {this.props.intl.locale === 'en' && (
          <div className={`${css.teamStatsGetTheApp} ${css.mobileView}`}>
            <TeamStatsGetTheApp leagueSlug={'mlb'} />
          </div>
        )}
      </div>
    );
  }

  render() {
    const event = normalizeBaseballEvent(this.props.event);
    const lastGame = event.last_matchup;
    const keyPlayers = formatKeyPlayers(event, 'P');
    const preGame = event.event_status === 'pre_game';
    const inProgress = event.event_status === 'in_progress';
    const suspended = event.event_status === 'suspended';
    const final = event.event_status === 'final';
    const postponedOrDelayed =
      event.event_status === 'postponed' || event.event_status === 'delayed';
    const hasLastPlay = getOr(event, 'box_score.last_play', {});
    const keyPlayersTitle = preGame ? 'Starting Pitchers' : 'Pitching';
    const inningSegment = getOr(event, 'box_score.progress.segment_division', '');
    const home = getOr(event, 'box_score.team_records.home', {});
    const away = getOr(event, 'box_score.team_records.away', {});
    const currentBatterRecord = getOr(event, 'box_score.current_batter_record', {});
    const displayLineScoreCard =
      (inProgress || suspended || final) &&
      (!postponedOrDelayed || Object.keys(hasLastPlay).length > 0);
    const hasHomePitcherData = inningSegment === 'top' && Object.keys(away).length > 0;
    const hasAwayPitcherData = inningSegment === 'bottom' && Object.keys(home).length > 0;
    const leagueSlug = getOr(this.props.event, 'league.slug', '');

    let batter;
    let batterStats;
    let pitcher;
    let pitcherStats;

    if (Object.keys(currentBatterRecord).length > 0) {
      batter = event.box_score.current_batter_record.player;
      batterStats = event.box_score.current_batter_hitting_split;
    }

    if (hasHomePitcherData) {
      pitcher = home.current_pitcher;
      pitcherStats = home.current_pitching_record;
    } else if (hasAwayPitcherData) {
      pitcher = away.current_pitcher;
      pitcherStats = away.current_pitching_record;
    }

    return (
      <div>
        <div className="row">
          <div className={`col-sm-8 ${css.column}`}>
            {renderMatchupCard(event)}
            {displayLineScoreCard ? renderLineScoreCard(event) : null}
            <div className={css.mobileView}>
              {preGame || final
                ? renderKeyPlayersCard(
                    keyPlayers,
                    keyPlayersTitle,
                    event.sport_name,
                    event.event_status
                  )
                : null}
            </div>

            {(inningSegment === 'top' || inningSegment === 'bottom') && pitcher ? (
              <ActivePlayer playerType="pitcher" player={pitcher} playerStats={pitcherStats} />
            ) : null}

            {(inningSegment === 'top' || inningSegment === 'bottom') && batter ? (
              <ActivePlayer playerType="batter" player={batter} playerStats={batterStats} />
            ) : null}

            {!preGame && event.scoring_plays && renderScoringSummary(event)}
            {preGame && Object.keys(this.state.battingLineup).length > 0 ? (
              <BaseballBattingLineup
                battingLineup={this.state.battingLineup}
                awayTeam={event.away_team.abbreviation}
                homeTeam={event.home_team.abbreviation}
              />
            ) : null}
            <div className={css.mobileView}>
              {preGame && renderTeamComparisonCard(event)}
              {renderGameDetailsCard(event)}
              {preGame && renderLastGame(lastGame)}
            </div>
            {preGame && event.home_stats_groups && this.renderAllTeamStats(event)}
          </div>
          <div className={`${css.desktopView} col-sm-4 ${css.column}`}>
            {renderGameDetailsCard(event)}
            <Ad leagueSlug={leagueSlug} adType={AdTypes.BIGBOX_DESKTOP} />
            {preGame || final
              ? renderKeyPlayersCard(
                  keyPlayers,
                  keyPlayersTitle,
                  event.sport_name,
                  event.event_status
                )
              : null}
            {preGame && renderTeamComparisonAndLastGame(event, lastGame)}
          </div>
        </div>
      </div>
    );
  }
}

MLBMatchup.propTypes = {
  event: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  stats: PropTypes.object.isRequired,
};

MLBMatchup.displayName = 'MLBMatchup';

export default injectIntl(StyledComponent(MLBMatchup, [css]));
